import { t as lnT } from '../i18n';
import {useTranslation} from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import styled, { createGlobalStyle } from 'styled-components';
import { IoMdClose } from 'react-icons/io';
import { QUERY } from '../gql-operations';

const TINYLAUNCH_URL = 'https://www.tinylaunch.com/launch/18402-luckynote';
const TINYLAUNCH_VOTING_END_ISO = '2026-08-31T07:00:00Z';
const TINYLAUNCH_HIDE_ISO = '2026-09-01T07:00:00Z';

const getTinyLaunchBannerState = () => {
    const diffMs = new Date(TINYLAUNCH_VOTING_END_ISO).getTime() - Date.now();

    if (diffMs <= 0) {
        return {
            status: 'thanks'
        };
    }

    const totalMinutes = Math.ceil(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return {
        status: 'voting',
        key: 'tinylaunch-2026',
        variant: 'INFO',
        dismissibleLoggedIn: true,
        dismissibleLoggedOut: false,
        ctaLabel: lnT('common:voteOnTinylaunch'),
        ctaUrl: TINYLAUNCH_URL,
        timeLeft: {
            hours,
            minutes: String(minutes).padStart(2, '0')
        }
    };
};

const getFallbackTinyLaunchBannerState = () => {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/solucky')) {
        return null;
    }

    const now = Date.now();
    const votingEndsAt = new Date(TINYLAUNCH_VOTING_END_ISO).getTime();
    const hideAt = new Date(TINYLAUNCH_HIDE_ISO).getTime();

    if (now >= hideAt) {
        return null;
    }

    if (now >= votingEndsAt) {
        return {
            status: 'thanks',
            key: 'tinylaunch-2026-thanks',
            variant: 'SUCCESS',
            dismissibleLoggedIn: true,
            dismissibleLoggedOut: false,
            message: lnT('common:thankYouForSupportingLuckynoteOnTinylaunch'),
            ctaLabel: lnT('common:viewOnTinylaunch'),
            ctaUrl: TINYLAUNCH_URL
        };
    }

    return getTinyLaunchBannerState();
};

const getCountdown = dateValue => {
    if (!dateValue) {
        return null;
    }

    const diffMs = new Date(dateValue).getTime() - Date.now();
    if (diffMs <= 0) {
        return null;
    }

    const totalMinutes = Math.ceil(diffMs / 60000);
    return {
        hours: Math.floor(totalMinutes / 60),
        minutes: String(totalMinutes % 60).padStart(2, '0')
    };
};

const isWithinBannerWindow = banner => {
    const now = Date.now();
    const startsAt = banner.startsAt ? new Date(banner.startsAt).getTime() : null;
    const hideAt = banner.hideAt ? new Date(banner.hideAt).getTime() : null;

    if (startsAt && now < startsAt) {
        return false;
    }

    if (hideAt && now >= hideAt) {
        return false;
    }

    return true;
};

const buildServerBannerState = banner => {
    if (!banner) {
        return null;
    }

    if (!isWithinBannerWindow(banner)) {
        return null;
    }

    const primaryCountdown = getCountdown(banner.primaryEndsAt);
    const isAfterPrimaryEnd = Boolean(banner.primaryEndsAt) && !primaryCountdown;
    const hasAfterEndContent = Boolean(banner.afterEndMessage || banner.afterEndCtaLabel || banner.afterEndCtaUrl);
    const status = isAfterPrimaryEnd && hasAfterEndContent ? 'thanks' : 'active';

    return {
        status,
        key: banner.key || banner.id,
        variant: banner.variant || 'INFO',
        dismissibleLoggedIn: banner.dismissibleLoggedIn,
        dismissibleLoggedOut: banner.dismissibleLoggedOut,
        message: status === 'thanks' && banner.afterEndMessage ? banner.afterEndMessage : banner.message,
        ctaLabel: status === 'thanks' && banner.afterEndCtaLabel ? banner.afterEndCtaLabel : banner.ctaLabel,
        ctaUrl: status === 'thanks' && banner.afterEndCtaUrl ? banner.afterEndCtaUrl : banner.ctaUrl,
        timeLeft: primaryCountdown
    };
};

const interpolateMessage = (message, timeLeft) => {
    if (!message) {
        return '';
    }

    if (!timeLeft) {
        return message.replace(/\s*\{\{timeLeft\}\}/g, '');
    }

    return message.replace(/\{\{timeLeft\}\}/g, `${timeLeft.hours}h ${timeLeft.minutes}m`);
};

function GlobalAnnouncementBanner({ isLoggedIn = false }) {
    const {t: lnT} = useTranslation();

    const bannerRef = useRef(null);
    const [, setTick] = useState(0);
    const [, setDismissedVersion] = useState(0);
    const { data, loading } = useQuery(QUERY.ACTIVE_ANNOUNCEMENT_BANNER, {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'ignore'
    });
    const [fallbackState, setFallbackState] = useState(getFallbackTinyLaunchBannerState);
    const serverState = buildServerBannerState(data?.activeAnnouncementBanner);
    const bannerState = serverState || (loading ? null : fallbackState);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setFallbackState(getFallbackTinyLaunchBannerState());
            setTick(tick => tick + 1);
        }, 60000);

        return () => window.clearInterval(intervalId);
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined' || !bannerState) {
            return undefined;
        }

        const dismissedKey = `announcement_banner_dismissed_${bannerState.key}`;
        try {
            if (localStorage.getItem(dismissedKey) === 'true') {
                return undefined;
            }
        } catch (error) {
            // If storage is unavailable, treat the banner as visible for layout purposes.
        }

        const updateBannerHeight = () => {
            const height = bannerRef.current?.offsetHeight || 0;
            document.documentElement.style.setProperty('--tinylaunch-banner-height', `${height}px`);
        };

        document.body.classList.add('tinylaunch-banner-visible');
        updateBannerHeight();

        const resizeObserver = typeof ResizeObserver !== 'undefined' && bannerRef.current
            ? new ResizeObserver(updateBannerHeight)
            : null;

        if (resizeObserver && bannerRef.current) {
            resizeObserver.observe(bannerRef.current);
        }

        window.addEventListener('resize', updateBannerHeight);

        return () => {
            document.body.classList.remove('tinylaunch-banner-visible');
            document.documentElement.style.removeProperty('--tinylaunch-banner-height');
            window.removeEventListener('resize', updateBannerHeight);
            resizeObserver?.disconnect();
        };
    }, [bannerState?.key]);

    const dismissBanner = React.useCallback(() => {
        if (!bannerState) {
            return;
        }

        const dismissedKey = `announcement_banner_dismissed_${bannerState.key}`;

        try {
            localStorage.setItem(dismissedKey, 'true');
        } catch (error) {
            // Ignore storage errors; dismissing for the current session is enough.
        }
        setDismissedVersion(version => version + 1);
    }, [lnT, bannerState?.key]);

    if (!bannerState) {
        return null;
    }

    const dismissedKey = `announcement_banner_dismissed_${bannerState.key}`;
    try {
        if (localStorage.getItem(dismissedKey) === 'true') {
            return null;
        }
    } catch (error) {
        // If storage is unavailable, keep the banner visible.
    }

    const bannerMessage = interpolateMessage(bannerState.message || 'Vote for Luckynote on TinyLaunch. Voting ends in {{timeLeft}}.', bannerState.timeLeft);
    const canDismiss = isLoggedIn
        ? bannerState.dismissibleLoggedIn
        : bannerState.dismissibleLoggedOut;

    return (
        <>
            <GlobalBannerOffset />
            <Banner ref={bannerRef} $variant={bannerState.variant}>
                <BannerContent>
                    <BannerText>
                        {bannerMessage}
                    </BannerText>
                    {bannerState.ctaUrl && bannerState.ctaLabel ? (
                        <BannerButton href={bannerState.ctaUrl} target="_blank" rel="noopener noreferrer">
                            {bannerState.ctaLabel}
                        </BannerButton>
                    ) : null}
                </BannerContent>
                {canDismiss ? (
                    <BannerDismiss
                        type="button"
                        onClick={dismissBanner}
                        aria-label={lnT('common:dismissTinylaunchVotingBanner')}
                    >
                        <IoMdClose size={15} />
                        <span>{lnT('common:dismiss')}</span>
                    </BannerDismiss>
                ) : null}
            </Banner>
        </>
    );
}

const GlobalBannerOffset = createGlobalStyle`
    body.tinylaunch-banner-visible .site-header {
        top: var(--tinylaunch-banner-height, 38px) !important;
    }

    body.tinylaunch-banner-visible.promo-banner-visible .site-header {
        top: calc(var(--tinylaunch-banner-height, 38px) + 48px) !important;
    }
`;

const Banner = styled.div`
    flex: 0 0 auto;
    width: 100%;
    min-height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 16px;
    background: ${({ $variant }) => ({
        SUCCESS: '#eefaf1',
        WARNING: '#fff7dc',
        ERROR: '#ffe4e2',
        INFO: '#eef5ff'
    }[$variant] || '#eef5ff')};
    border-bottom: 1px solid ${({ $variant }) => ({
        SUCCESS: '#a7dfb2',
        WARNING: '#efd27b',
        ERROR: '#df6862',
        INFO: '#a8c9f0'
    }[$variant] || '#a8c9f0')};
    color: ${({ $variant }) => ({
        SUCCESS: '#17351d',
        WARNING: '#1f1f1f',
        ERROR: '#6b1f1b',
        INFO: '#17304f'
    }[$variant] || '#17304f')};
    font-size: 14px;
    line-height: 1.3;
    text-align: center;
    box-sizing: border-box;
    position: relative;
    z-index: 1001;

    @media (max-width: 560px) {
        padding: 12px 44px 12px 16px;
    }
`;

const BannerContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-width: 0;

    @media (max-width: 560px) {
        flex-direction: column;
        gap: 10px;
    }
`;

const BannerText = styled.span`
    font-weight: 600;

    @media (max-width: 560px) {
        max-width: 300px;
        font-size: 16px;
        line-height: 1.28;
    }
`;

const BannerButton = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 26px;
    padding: 0 12px;
    border-radius: 6px;
    background: #141414;
    color: #ffffff !important;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none !important;
    white-space: nowrap;

    @media (max-width: 560px) {
        min-height: 34px;
        padding: 0 18px;
        font-size: 14px;
    }
`;

const BannerDismiss = styled.button`
    position: absolute;
    top: 50%;
    right: 12px;
    min-height: 24px;
    padding: 0 8px;
    border: 1px solid rgba(20, 20, 20, 0.22);
    border-radius: 6px;
    background: transparent;
    color: #1f1f1f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transform: translateY(-50%);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.01em;
    gap: 4px;
    transition: border-color 0.15s ease, transform 0.15s ease;

    &:hover {
        border-color: rgba(20, 20, 20, 0.35);
        transform: translateY(-50%) translateY(-1px);
    }

    span {
        display: inline-block;
    }

    @media (max-width: 560px) {
        top: 10px;
        right: 10px;
        width: 28px;
        min-height: 28px;
        padding: 0;
        transform: none;

        span {
            display: none;
        }

        &:hover {
            transform: none;
        }
    }
`;

export default GlobalAnnouncementBanner;
